import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { clearSentryUser, lastSentryEventId, setSentryUser } from "../../sentry.config";

import { ErrorDetails } from "@/components/error-details";
import { ErrorReporterProvider, useErrorReporter } from "@/components/error-reporter";
import { describeError } from "@/lib/errors";
import appCss from "../styles.css?url";
import logoSrc from "@/assets/logo.png";
import { supabase } from "@/integrations/supabase/client";
import { Footer } from "@/components/footer";
import { THEME_COLORS, themeInitScript } from "@/lib/theme";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-text">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-text">Page not found</h2>
        <p className="mt-2 text-sm text-muted">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Root route error boundary.
 *
 * This component is rendered *instead of* the root route component, which
 * means it cannot rely on the `ErrorReporterProvider` that `RootComponent`
 * mounts -- if `RootComponent` itself threw, that provider was never mounted.
 * So the boundary owns a provider of its own, and the inner screen reads the
 * reporter through it.
 */
function ErrorComponent({ error, reset }) {
  return (
    <ErrorReporterProvider>
      <RouteErrorScreen error={error} reset={reset} />
    </ErrorReporterProvider>
  );
}

function RouteErrorScreen({ error, reset }) {
  const router = useRouter();
  const { recordError, lastError } = useErrorReporter();

  // The router's `defaultOnCatch` (see src/router.jsx) already reported this
  // error to Sentry, so this only feeds the superadmin details panel. It runs
  // in an effect rather than during render because the boundary re-renders on
  // every navigation attempt while the error is active. The event id is read
  // for that panel and never rendered to the visitor.
  useEffect(() => {
    recordError(error, { area: "route", eventId: lastSentryEventId() });
  }, [error, recordError]);

  // Normalized copy, so a route error that crossed a server-function boundary
  // shows its catalog message ("That game code isn't right") instead of a
  // framework string.
  const view = describeError(lastError?.error ?? error, { isSuperadmin: false });

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-text">{view.title}</h1>
        <p className="mt-2 text-sm text-muted">{view.message}</p>
        {view.hint && <p className="mt-1 text-sm text-muted">{view.hint}</p>}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-bg px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-hover"
          >
            Go home
          </a>
        </div>

        {/* Renders nothing unless the viewer is a superadmin. */}
        <ErrorDetails className="mt-6 text-left" />
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Subiza | Live Quiz Platform" },
      {
        name: "description",
        content:
          "Subiza is a real-time AI-powered live quiz platform for schools where teachers host live games and students join with a PIN.",
      },
      { name: "author", content: "Elisee MUGIRANEZA" },
      { name: "creator", content: "Elisee MUGIRANEZA" },
      { name: "founders", content: "Elisee MUGIRANEZA, Moise NIYOMAHORO" },
      { name: "copyright", content: "Subiza by Elisee MUGIRANEZA and Moise NIYOMAHORO" },
      { name: "google-site-verification", content: "-v7hv6CqHCVSTT1V7mj0C4CbNZPk0imtagdkhJwZzKU" },
      { property: "og:site_name", content: "Subiza" },
      { property: "og:title", content: "Subiza - Real-Time AI Quiz Platform" },
      {
        property: "og:description",
        content: "Subiza was founded by Elisee MUGIRANEZA and Moise NIYOMAHORO.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://subiza.pages.dev" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Subiza - Real-Time AI Quiz Platform" },
      {
        name: "twitter:description",
        content: "Subiza was founded by Elisee MUGIRANEZA and Moise NIYOMAHORO.",
      },
      { property: "og:image", content: logoSrc },
      { name: "twitter:image", content: logoSrc },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        href: logoSrc,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Subiza",
          description: "Real-time AI-powered live quiz platform for schools.",
          founder: [
            {
              "@type": "Person",
              name: "Elisee MUGIRANEZA",
              role: "Founder & Lead Developer",
              url: "https://eliseemugiraneza.pages.dev/",
            },
            {
              "@type": "Person",
              name: "Moise NIYOMAHORO",
              role: "Co-Founder",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* theme-color and the no-flash script must stay together and ahead of
            HeadContent, so the meta is parsed before the inline script runs and
            the browser paints with the right palette on first frame. */}
        <meta name="theme-color" content={THEME_COLORS.light} />
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Attribute Sentry events to the signed-in account. Without this, every
      // error is an anonymous user and Sentry cannot report "N users affected",
      // which is the number that decides whether an incident is a priority.
      //
      // This runs before the event filter on purpose: Supabase emits
      // INITIAL_SESSION on subscribe, so a returning user is attributed from
      // their first error. Gating on SIGNED_IN alone would leave the entire
      // first page view anonymous.
      //
      // Roles are intentionally not resolved here: they live in the
      // `user_roles` table, and a lookup on every root mount is not worth the
      // request. Pass `role` to setSentryUser from a screen that already has it.
      if (session?.user) {
        setSentryUser({ id: session.user.id, email: session.user.email });
      } else if (event === "SIGNED_OUT") {
        // Must clear explicitly: otherwise events from the next anonymous
        // visitor are attributed to the account that just signed out.
        clearSentryUser();
      }

      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <ErrorReporterProvider>
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Footer />
        {/* Superadmin-only; renders nothing for every other role. */}
        <ErrorDetails className="mx-auto mb-4 w-full max-w-2xl px-4" />
        <Toaster position="top-center" richColors />
      </QueryClientProvider>
    </ErrorReporterProvider>
  );
}
